import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { getCroppedImg } from '../../utils/cropImage';
import { FiRotateCcw, FiRotateCw, FiXSquare } from 'react-icons/fi';
import { IoIosPhoneLandscape, IoIosPhonePortrait, IoIosSquareOutline } from 'react-icons/io';
import { MdFlip } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import './ImageCropper.css';

const aspectIcons = {
  landscape: IoIosPhoneLandscape,
  square: IoIosSquareOutline,
  portrait: IoIosPhonePortrait,
};

const ImageCropper = ({
  image,
  onUploadSuccess,
  onUpload,
  onCancel,
  aspect = 1,
  title = null,
  aspectOptions = [],
}) => {
  const { t } = useTranslation();

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [selectedAspect, setSelectedAspect] = useState(aspect);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [flip, setFlip] = useState({ horizontal: false, vertical: false });
  const [uploading, setUploading] = useState(false);

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const uploadCroppedImage = async () => {
    if (uploading) return;
    setUploading(true);
    try {
      const blob = await getCroppedImg(image, croppedAreaPixels, rotation, flip);

      const formData = new FormData();
      formData.append('image', blob, 'photo.jpg');

      const result = await onUpload(formData);

      if (result?.success) {
        onUploadSuccess(result.photo || null);
      } else {
        alert(t('common.form.errors.general'));
      }
    } catch (err) {
      console.error('ImageCropper error:', err);
      if (err instanceof Error) {
        alert(`${t('user_profile_page.errors.process_image')}: ${err.message}`);
      } else if (err?.data?.errors?.length) {
        alert(err.data.errors.join(', '));
      } else if (err?.status) {
        alert(`${t('user_profile_page.errors.process_image')} (HTTP ${err.status})`);
      } else {
        alert(t('user_profile_page.errors.process_image'));
      }
    } finally {
      setUploading(false);
    }
  };

  const createTransform = () => {
    const scaleX = flip.horizontal ? -1 : 1;
    const scaleY = flip.vertical ? -1 : 1;

    return `translate(${crop.x}px, ${crop.y}px) rotate(${rotation}deg) scale(${zoom * scaleX}, ${zoom * scaleY})`;
  };

  return (
    <div className="overlay">
      <div className="cropper-modal">
        {title && <h4>{title}</h4>}
        <div style={{ position: 'relative', width: '100%', height: 400 }}>
          <Cropper
            image={image}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={selectedAspect}
            cropShape="rect"
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onRotationChange={setRotation}
            onCropComplete={onCropComplete}
            transform={createTransform()}
          />
        </div>

        <div className="cropper-controls">
          <div className="cropper-primary-controls">
            {aspectOptions.length > 0 && (
              <div className="aspect-options-container">
                <div className="aspect-options">
                  {aspectOptions.map((option) => {
                    const AspectIcon = aspectIcons[option.icon] || IoIosSquareOutline;

                    return (
                      <button
                        key={option.label}
                        type="button"
                        className={`btn btn-light aspect-option${selectedAspect === option.value ? ' active' : ''}`}
                        onClick={() => setSelectedAspect(option.value)}
                        disabled={uploading}
                        aria-pressed={selectedAspect === option.value}
                        aria-label={option.label}
                        title={option.label}
                      >
                        <AspectIcon size={20} aria-hidden="true" />
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="zoom-container">
              <span>{t('user_profile_page.zoom_label')}</span>
              <input
                type="range"
                min={1}
                max={3}
                step={0.1}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
              />
            </div>
          </div>

          <div className="rotation-container">
            <button
              type="button"
              className="btn btn-light"
              onClick={() => setRotation((r) => r - 90)}
              title={t('user_profile_page.rotate_left')}
            >
              <FiRotateCcw size={15} />
            </button>

            <button
              type="button"
              className="btn btn-light"
              onClick={() => setRotation(0)}
              title={t('user_profile_page.reset_rotation')}
            >
              <FiXSquare size={10} />
            </button>
            <button
              type="button"
              className="btn btn-light"
              onClick={() => setRotation((r) => r + 90)}
              title={t('user_profile_page.rotate_right')}
            >
              <FiRotateCw size={15} />
            </button>
            <button
              type="button"
              className="btn btn-light"
              onClick={() => setFlip((prev) => ({ ...prev, horizontal: !prev.horizontal }))}
              title={t('user_profile_page.flip_horizontal')}
            >
              <MdFlip size={15} />
            </button>
          </div>
        </div>

        <div className="submission-container">
          <button type="button" className="btn btn-primary" onClick={uploadCroppedImage} disabled={uploading}>
            {uploading ? t('common.loading') || '...' : t('common.confirm')}
          </button>

          <button type="button" className="btn btn-outline-secondary" onClick={onCancel} disabled={uploading}>
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageCropper;
